/**
 * SetupWizardCard Component
 *
 * Reusable card component for developer tool setup wizards.
 */
import React from 'react';
import {
  Card,
  CardHeader,
  Text,
  Button,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ChevronRightRegular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  card: {
    width: '100%',
    cursor: 'pointer',
    transition: 'box-shadow 0.2s',
    ':hover': {
      boxShadow: tokens.shadow8,
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    flex: 1,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
  },
});

export interface SetupWizardCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  badge?: string;
  badgeColor?: 'brand' | 'success' | 'warning' | 'danger' | 'informative';
  onClick: () => void;
}

export const SetupWizardCard: React.FC<SetupWizardCardProps> = ({
  title,
  description,
  icon,
  badge,
  badgeColor = 'informative',
  onClick,
}) => {
  const styles = useStyles();

  return (
    <Card className={styles.card} onClick={onClick}>
      <CardHeader
        image={<div className={styles.iconContainer}>{icon}</div>}
        header={
          <div className={styles.content}>
            <div className={styles.titleRow}>
              <Text weight="semibold" size={400}>
                {title}
              </Text>
              {badge && (
                <Badge appearance="filled" color={badgeColor} size="medium">
                  {badge}
                </Badge>
              )}
            </div>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              {description}
            </Text>
          </div>
        }
        action={
          <Button
            appearance="subtle"
            icon={<ChevronRightRegular />}
            aria-label="Open setup wizard"
          />
        }
      />
    </Card>
  );
};
